import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import {
  ADMIN_TEST_PUSH_DEFAULT_MESSAGE,
  ApiError,
} from '@ivy-monorepo/shared'
import { BaseModalProps } from 'src/types/modal'
import { adminService } from '../../services/admin.service'

interface SendTestPushModalProps extends BaseModalProps {
  userPublicId: string
  userName?: string
  hasDeviceToken: boolean
  hasValidOneSignalToken: boolean
}

const SendTestPushModal = ({
  isOpen,
  onClose,
  userPublicId,
  userName,
  hasDeviceToken,
  hasValidOneSignalToken,
}: SendTestPushModalProps) => {
  const [message, setMessage] = useState(ADMIN_TEST_PUSH_DEFAULT_MESSAGE)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setMessage(ADMIN_TEST_PUSH_DEFAULT_MESSAGE)
    setError(null)
    setSuccess(null)
  }, [isOpen])

  const canSend = hasValidOneSignalToken && message.trim().length > 0 && !isPending

  const tokenHint = !hasDeviceToken
    ? 'This user has no OneSignal device token, so a push cannot be sent.'
    : !hasValidOneSignalToken
      ? 'This user has a device token, but it is not a valid OneSignal subscription UUID.'
      : null

  const handleSend = async () => {
    const trimmed = message.trim()
    if (!trimmed || !canSend) {
      return
    }

    try {
      setIsPending(true)
      setError(null)
      setSuccess(null)
      const result = await adminService.sendTestPush(userPublicId, trimmed)
      const recipientLabel =
        typeof result.recipients === 'number' ? ` Recipients: ${result.recipients}.` : ''
      setSuccess(`Sent. OneSignal id: ${result.oneSignalNotificationId}.${recipientLabel}`)
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.friendlyMessage || apiError.message || 'Failed to send test push.')
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {
        if (!isPending) {
          onClose()
        }
      }}
      ariaHideApp={false}
      className="modal-popup confirm-popup"
      overlayClassName="modal-overlay"
      shouldCloseOnEsc={!isPending}
      shouldCloseOnOverlayClick={!isPending}
    >
      <div className="modal-content-wrapper">
        <h2>Send test push</h2>
        {userName ? (
          <p style={{ marginBottom: '12px', color: '#555' }}>
            Sends a visible OneSignal notification to <strong>{userName}</strong>.
          </p>
        ) : (
          <p style={{ marginBottom: '12px', color: '#555' }}>
            Sends a visible OneSignal notification to this user.
          </p>
        )}
        {tokenHint ? (
          <div className="page-error" style={{ marginBottom: '12px' }}>
            {tokenHint}
          </div>
        ) : null}
        <div className="form-group">
          <label htmlFor="admin-test-push-message">Message</label>
          <textarea
            id="admin-test-push-message"
            value={message}
            onChange={event => setMessage(event.target.value)}
            rows={4}
            disabled={isPending || !hasValidOneSignalToken}
          />
        </div>
        {error ? <div className="page-error">{error}</div> : null}
        {success ? <div style={{ color: 'var(--success-color, green)' }}>{success}</div> : null}
        <div className="modal-footer-content">
          <button type="button" className="styled-button" onClick={onClose} disabled={isPending}>
            {success ? 'Close' : 'Cancel'}
          </button>
          <button type="button" className="styled-button" onClick={handleSend} disabled={!canSend}>
            {isPending ? 'Sending…' : 'Send test push'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default SendTestPushModal
